import React, { MouseEventHandler, MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { BodyText, Header2Text } from '../Typography';
import { FlexRow } from '../FlexRow';
import { IconSize, IconType } from '../Icon';
import { StyledCard, StyledCardCollapse, StyledCardContentWrapper } from './Card.Styled';
import { mergeTests } from '../../test-utilities/mergeTests';
import NoPermissionsErrorMessage from '../../../components/shared/permissions/NoPermissionsErrorMessage';
import { IHasTestData } from '../../test-utilities/IHasTestData';

export type TActionsRenderFunction = (state: { isCollapsed: boolean }) => ReactNode;

export interface ICardProps extends IHasTestData {
  /** Additional CSS class. */
  className?: string;
  /** Displays the component as an absolute element. */
  modal?: boolean;
  /** Is this card collapsible */
  isCollapsible?: boolean;
  /** Heading text. */
  title?: string | ReactNode;
  /** Heading text or component. */
  footer?: string | ReactNode;
  /** Header text when collapsed */
  collapsedTitle?: string | ReactNode;
  /** Actions in the upper right corner of the card*/
  actions?: TActionsRenderFunction | ReactNode;
  /** Children to display in a Card */
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  hasEditPermissions?: boolean;
  contentPadding?: string;
}

export const Card = React.forwardRef<HTMLDivElement | null, ICardProps>((props, ref) => {
  const {
    className,
    modal,
    title,
    collapsedTitle,
    children,
    actions,
    isCollapsible = false,
    'data-test-id': dataTestID,
    onClick,
    hasEditPermissions,
    footer,
  } = props;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [titleToShow, setTitleToShow] = useState(title);
  useEffect(() => {
    if (!isCollapsed) {
      setTitleToShow(title);
    }
  }, [title, isCollapsed]);
  const [hideOverflow, setHideOverflow] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const mergedRef = useCallback(
    (node: any) => {
      cardRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref]
  );

  // These few things deal with the height of the card at its maximum so we can collapse is smoothly.
  const [height, setHeight] = useState(0);

  const clickCollapse = () => {
    if (cardRef.current) {
      if (isCollapsible) {
        setHideOverflow(true);
        if (!isCollapsed) {
          const newHeight = cardRef.current.getBoundingClientRect().height;
          setHeight(newHeight);
        }
        setTimeout(() => {
          setIsCollapsed(!isCollapsed);
        });
      }
      setTitleToShow(title);
    }
  };

  const onTransitionEnd = () => {
    if (isCollapsed) {
      setTitleToShow(collapsedTitle || title);
    } else {
      setTitleToShow(title);
      setHeight(0);
      setHideOverflow(false);
    }
  };

  return (
    <StyledCard
      hideOverflow={hideOverflow}
      className={className}
      ref={mergedRef}
      isModal={modal}
      isCollapsed={isCollapsed}
      isCollapsible={isCollapsible}
      height={height}
      onTransitionEnd={onTransitionEnd}
      onClick={onClick}
      data-test-id={mergeTests(dataTestID, 'wrapper')}
    >
      <StyledCardContentWrapper isCollapsible={isCollapsible} contentPadding={props.contentPadding}>
        {isCollapsible && (title || actions) && (
          <StyledCardCollapse
            type={IconType.ArrowDown}
            size={IconSize.Small}
            onClick={clickCollapse}
            isCollapsed={isCollapsed}
            data-test-id={mergeTests(dataTestID, 'collapse')}
          />
        )}
        {title || actions ? (
          <FlexRow justifyContent={'space-between'}>
            {titleToShow ? (
              typeof titleToShow === 'string' ? (
                <Header2Text
                  style={{ cursor: isCollapsible ? 'pointer' : undefined }}
                  onClick={isCollapsible ? clickCollapse : undefined}
                  data-test-id={mergeTests(dataTestID, 'title')}
                >
                  {titleToShow} &nbsp;
                  <NoPermissionsErrorMessage hasPermissions={hasEditPermissions} />
                </Header2Text>
              ) : (
                <FlexRow
                  style={{ cursor: isCollapsible ? 'pointer' : undefined }}
                  verticalAlign={'baseline'}
                  onClick={isCollapsible ? clickCollapse : undefined}
                  data-test-id={mergeTests(dataTestID, 'title')}
                >
                  {titleToShow}
                </FlexRow>
              )
            ) : null}
            {typeof actions === 'function' ? actions({ isCollapsed }) : actions}
          </FlexRow>
        ) : null}
        <div>{children}</div>
      </StyledCardContentWrapper>
      {typeof footer === 'string' ? <BodyText>{footer}</BodyText> : footer}
    </StyledCard>
  );
});
